import React, { Fragment, useEffect, useState } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  CardHeader,
  Badge,
  Modal,
  ModalHeader,
  ModalBody,
  Table,
  Button,
  ModalFooter,
} from "reactstrap";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";

import { titleCase, sentenceCase } from "../../utils/case";
import DataTables from "../../Components/Tables/DataTable";
import { Breadcrumbs } from "../../AbstractElements";

import {
  getTransactions,
  updateTransaction,
} from "../../api/transactions.api";

import { Eye, Check, X, File, Mail } from "react-feather";
import { formatDateTime } from "../../utils/datetime";
import Swal from "sweetalert2";
import { sendReceiptEmail } from "../../api/receipt.api";

const TransactionMain = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);

  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [viewModalOpen, setViewModalOpen] = useState(false);

  const [meta, setMeta] = useState({
    total: 0,
    page: 1,
    perPage: 10,
    totalPages: 1,
  });

  const [sortConfig, setSortConfig] = useState({
    sortBy: "createdAt",
    sortOrder: "DESC",
  });

  const [filters, setFilters] = useState({
    bookingRef: "",
    status: "",
    paymentChannel: "",
    startDate: "",
    endDate: "",
  });

  const fetchTransactions = async (
    page = 1,
    perPage = 10,
    sortBy = sortConfig.sortBy,
    sortOrder = sortConfig.sortOrder,
    appliedFilters = filters
  ) => {
    try {
      setLoading(true);

      const res = await getTransactions({
        page,
        perPage,
        sortBy,
        sortOrder,
        filters: appliedFilters,
      });

      setData(res?.data || []);
      setMeta(
        res?.meta || {
          total: 0,
          page,
          perPage,
          totalPages: 1,
        }
      );
    } catch (err) {
      toast.error(err.message || "failed to fetch transactions");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTransactions(meta.page, meta.perPage);
  }, []);

  const handlePageChange = (newPage) => {
    fetchTransactions(
      newPage,
      meta.perPage,
      sortConfig.sortBy,
      sortConfig.sortOrder,
      filters
    );
  };

  const handlePerPageChange = (newPerPage) => {
    fetchTransactions(
      1,
      newPerPage,
      sortConfig.sortBy,
      sortConfig.sortOrder,
      filters
    );
  };

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const applyFilters = () => {
    fetchTransactions(
      1,
      meta.perPage,
      sortConfig.sortBy,
      sortConfig.sortOrder,
      filters
    );
  };

  const resetFilters = () => {
    const cleared = {
      bookingRef: "",
      status: "",
      paymentChannel: "",
      startDate: "",
      endDate: "",
    };

    setFilters(cleared);

    fetchTransactions(1, meta.perPage, sortConfig.sortBy, sortConfig.sortOrder, cleared);
  };

  const handleView = (row) => {
    setSelectedTransaction(row);
    setViewModalOpen(true);
  };

  const handleCloseModal = () => {
    setViewModalOpen(false);
    setSelectedTransaction(null);
  };

  const handleUpdateTransaction = async (row, status) => {
    // Step 1: Confirm update
    const confirmed = await Swal.fire({
      title: sentenceCase(t("are you sure you want to update this transaction?")),
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: titleCase(t("confirm")),
      cancelButtonText: titleCase(t("cancel")),
    });

    if (!confirmed.isConfirmed) return;

    try {
      // Step 2: Update transaction
      const res = await updateTransaction(row.id, { status });

      toast.success(t("transaction updated"));

      // 🔥 IMPORTANT: get updated transaction (with receipt)
      const updatedTransaction = res;

      // Refresh table
      await fetchTransactions(meta.page, meta.perPage);

      // Step 3: Only proceed if success + receipt exists
      if (status === "success" && updatedTransaction?.receipt) {
        const emailPrompt = await Swal.fire({
          title: titleCase(t("send receipt email?")),
          text: sentenceCase(
            t("do you want to email the receipt to the customer")
          ),
          icon: "question",
          showCancelButton: true,
          confirmButtonText: titleCase(t("yes")),
          cancelButtonText: titleCase(t("no")),
        });

        if (emailPrompt.isConfirmed) {
          try {
            await sendReceiptEmail(updatedTransaction.receipt.id);

            toast.success(t("receipt email sent"));
          } catch (err) {
            toast.error(err.message || "failed to send email");
          }
        }
      }
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleViewSlip = (url) => {
    if (!url) return;
    window.open(`https://bfiles.chinfumc.org.my${url}`, "_blank");
  };

  const handleSort = (column, direction) => {
    const sortBy = column.sortField || "createdAt";
    const sortOrder = direction === "asc" ? "ASC" : "DESC";

    setSortConfig({ sortBy, sortOrder });

    fetchTransactions(meta.page, meta.perPage, sortBy, sortOrder);
  };

  const handleSendReceiptEmail = async (row) => {
    if (!row?.receipt?.id) {
      toast.error(t("no receipt available"));
      return;
    }

    const confirm = await Swal.fire({
      title: titleCase(t("send receipt email?")),
      icon: "question",
      showCancelButton: true,
      confirmButtonText: titleCase(t("yes")),
      cancelButtonText: titleCase(t("cancel")),
    });

    if (!confirm.isConfirmed) return;

    try {
      await sendReceiptEmail(row.receipt.id);
      toast.success(t("receipt email sent"));
    } catch (err) {
      toast.error(err.message || "failed to send email");
    }
  };

  const columns = [
    {
      name: titleCase(t("Trx No")),
      selector: (row) => row.refNo,
      sortable: true,
      sortField: "refNo",
    },
    {
      name: titleCase(t("reservation")),
      selector: (row) => row.reservation?.bookingRef || "-",
      sortable: true,
      cell: (row) => {
        if (!row.reservation) return "-";

        return (
          <span
            style={{ color: "blue", cursor: "pointer", textDecoration: "underline" }}
            onClick={() =>
              navigate(`/reservations/view/${row.reservation.id}`)
            }
          >
            {row.reservation.bookingRef}
          </span>
        );
      },
      sortField: "bookingRef",
    },
    {
      name: titleCase(t("amount")),
      selector: (row) => row.amount,
    },
    {
      name: titleCase(t("payment method")),
      selector: (row) =>
        sentenceCase(t(row.paymentChannel || "-")),
      sortable: true,
      sortField: "paymentChannel"
    },
    {
      name: titleCase(t("status")),
      selector: (row) => row.status,
      center: true,
      cell: (row) => {
        let color = "secondary";
        if (row.status === "pending") color = "warning";
        else if (row.status === "paid") color = "primary";
        else if (row.status === "failed") color = "danger";

        return (
          <div style={{ width: "100%", textAlign: "center" }}>
            <Badge color={color}>
              {titleCase(t(row.status))}
            </Badge>
          </div>
        );
      },
      sortable: true,
      sortField: "status",
    },
    {
      name: titleCase(t("created at")),
      selector: (row) => formatDateTime(row.createdAt),
      sortable: true,
      sortField: "createdAt",
      sortField: "createdAt",
    },
    {
      name: titleCase(t("action")),
      cell: (row) => (
        <div className="d-flex gap-2 align-items-center">
          {/* View */}
          <Eye
            size={18}
            style={{ cursor: "pointer", color: "blue" }}
            onClick={() => handleView(row)}
            title={t("view")}
          />
          
          {/* Download Receipt */}
          {row.receipt && (
            <File
              size={18}
              style={{ cursor: "pointer", color: "brown" }}
              title={t("receipt")}
              onClick={() => window.open(`https://bfiles.chinfumc.org.my${row.receipt.documentUrl}`, "_blank")}
            />
          )}

          {/* Send Receipt Email */}
          {row.receipt?.documentUrl && (
            <Mail
              size={18}
              style={{ cursor: "pointer", color: "purple" }}
              title={t("send receipt email")}
              onClick={() => handleSendReceiptEmail(row)}
            />
          )}

          {/* Only show actions if pending */}
          {row.status === "pending" && (
            <>
              <Check
                size={18}
                style={{ cursor: "pointer", color: "green" }}
                onClick={() =>
                  handleUpdateTransaction(row, "success")
                }
                title={t("confirm payment")}
              />

              <X
                size={18}
                style={{ cursor: "pointer", color: "red" }}
                onClick={() =>
                  handleUpdateTransaction(row, "fail")
                }
                title={t("invalid payment")}
              />
            </>
          )}
        </div>
      ),
    },
  ];

  return (
    <Fragment>
      <Breadcrumbs
        parent={titleCase(t("transactions"))}
        title={titleCase(t("transaction management"))}
        mainTitle={titleCase(t("transaction management"))}
      />

      <Container fluid>
        <Row>
          <Col md="12">
            <Card>
              <CardHeader />

              <CardBody>
                <Row className="mb-3 align-items-end">
                  <Col md="2">
                    <label>{titleCase(t("booking ref"))}</label>
                    <input
                      type="text"
                      className="form-control"
                      value={filters.bookingRef}
                      onChange={(e) =>
                        handleFilterChange("bookingRef", e.target.value)
                      }
                      placeholder="Search booking ref"
                    />
                  </Col>

                  <Col md="2">
                    <label>{titleCase(t("status"))}</label>
                    <select
                      className="form-control"
                      value={filters.status}
                      onChange={(e) => handleFilterChange("status", e.target.value)}
                    >
                      <option value="">{titleCase(t("all"))}</option>
                      <option value="pending">{titleCase(t("pending"))}</option>
                      <option value="success">{titleCase(t("success"))}</option>
                      <option value="fail">{titleCase(t("fail"))}</option>
                    </select>
                  </Col>

                  {/* <Col md="2">
                    <label>{titleCase(t("payment method"))}</label>
                    <select
                      className="form-control"
                      value={filters.paymentChannel}
                      onChange={(e) =>
                        handleFilterChange("paymentChannel", e.target.value)
                      }
                    >
                      <option value="">{titleCase(t("all"))}</option>
                      <option value="bank_transfer">{titleCase(t("bank transfer"))}</option>
                      <option value="spay_global">{titleCase(t("S Pay Global"))}</option>
                      <option value="fpx">{titleCase(t("FPX"))}</option>
                    </select>
                  </Col> */}

                  <Col md="2">
                    <label>{titleCase(t("start date"))}</label>
                    <input
                      type="date"
                      className="form-control"
                      value={filters.startDate}
                      onChange={(e) =>
                        handleFilterChange("startDate", e.target.value)
                      }
                    />
                  </Col>

                  <Col md="2">
                    <label>{titleCase(t("end date"))}</label>
                    <input
                      type="date"
                      className="form-control"
                      value={filters.endDate}
                      onChange={(e) =>
                        handleFilterChange("endDate", e.target.value)
                      }
                    />
                  </Col>

                  <Col md="4" className="mt-2">
                    <Button className="me-2" size="sm" color="primary" outline onClick={applyFilters}>
                      {titleCase(t("apply"))}
                    </Button>
                    <Button color="secondary" size="sm" outline onClick={resetFilters}>
                      {titleCase(t("reset"))}
                    </Button>
                  </Col>
                </Row>
                <DataTables
                  initialData={data}
                  columns={columns}
                  loading={loading}
                  meta={meta}
                  onPageChange={handlePageChange}
                  onPerPageChange={handlePerPageChange}
                  onSort={handleSort}
                  sortServer
                />
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>

      {/* ================= VIEW MODAL ================= */}
      <Modal
        isOpen={viewModalOpen}
        toggle={handleCloseModal}
        size="md"
      >
        <ModalHeader toggle={handleCloseModal}>
          {titleCase(t("transaction details"))}
        </ModalHeader>

        <ModalBody>
          {selectedTransaction && (
            <div
              style={{
                border: "1px dashed #999",
                borderRadius: "6px",
                padding: "15px",
              }}
            >
              <Table borderless responsive>
                <tbody>
                  <tr>
                    <td>{titleCase(t("transaction ID"))}</td>
                    <td>{selectedTransaction.id}</td>
                  </tr>
                  
                  {selectedTransaction.reservation && (
                    <tr>
                      <td>{titleCase(t("reservation"))}</td>
                      <td>
                        <span
                          style={{
                            color: "blue",
                            cursor: "pointer",
                            textDecoration: "underline",
                          }}
                          onClick={() =>
                            navigate(
                              `/reservations/view/${selectedTransaction.reservation.id}`
                            )
                          }
                        >
                          {selectedTransaction.reservation.bookingRef}
                        </span>
                      </td>
                    </tr>
                  )}

                  <tr>
                    <td>{titleCase(t("amount"))}</td>
                    <td>{selectedTransaction.amount}</td>
                  </tr>

                  <tr>
                    <td>{titleCase(t("payment method"))}</td>
                    <td>
                      {sentenceCase(
                        t(selectedTransaction.paymentChannel || "-")
                      )}
                    </td>
                  </tr>

                  {selectedTransaction.refNo !== "-" && (
                    <tr>
                      <td>{titleCase(t("reference no"))}</td>
                      <td>{selectedTransaction.refNo || "-"}</td>
                    </tr>
                  )}

                  <tr>
                    <td>{titleCase(t("status"))}</td>
                    <td>
                      {titleCase(t(selectedTransaction.status))}
                    </td>
                  </tr>

                  <tr>
                    <td>{titleCase(t("created at"))}</td>
                    <td>{formatDateTime(selectedTransaction.createdAt)}</td>
                  </tr>

                  {selectedTransaction.note && (
                    <tr>
                      <td>{titleCase(t("note"))}</td>
                      <td>{selectedTransaction.note}</td>
                    </tr>
                  )}
                  {/* Payment Proof Row */}
                  {selectedTransaction.paymentProof && (
                    <tr>
                      <td>{titleCase(t("transaction slip"))}</td>
                      <td>
                        <Button
                          color="primary"
                          size="sm"
                          onClick={() =>
                            handleViewSlip(
                              selectedTransaction.paymentProof
                            )
                          }
                        >
                          {titleCase(t("view transaction slip"))}
                        </Button>
                      </td>
                    </tr>
                  )}
                  {/* Payment Proof Row */}
                  {selectedTransaction.receipt && (
                    <tr>
                      <td>{titleCase(t("receipt"))}</td>
                      <td>
                        <Button
                          color="primary"
                          size="sm"
                          onClick={() => window.open(`https://bfiles.chinfumc.org.my${selectedTransaction.receipt.documentUrl}`, "_blank")}
                        >
                          {titleCase(t("download receipt"))}
                        </Button>
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>
          )}
        </ModalBody>
        {/* ✅ FOOTER WITH ACTION */}
        {selectedTransaction?.status === "pending" && (
          <ModalFooter>
            <Button
              color="success"
              onClick={() =>
                handleUpdateTransaction(selectedTransaction, "success")
              }
            >
              {titleCase(t("confirm payment"))}
            </Button>

            <Button
              color="danger"
              onClick={() =>
                handleUpdateTransaction(selectedTransaction, "fail")}
            >
              {titleCase(t("reject payment"))}
            </Button>
          </ModalFooter>
        )}
      </Modal>
    </Fragment>
  );
};

export default TransactionMain;